/**
 * Transforms an Account Leadsheet 294 V2 trial balance response into a plain
 * normalized shape (arrays per bucket + metadata) that the slice can feed into
 * adapter `setAll` calls. This module performs no adapter mutations — the slice
 * owns all `setAll` / `getInitialState` calls.
 */
import {FS_TYPE_IDS, getReferenceGroupRowLabel} from '../../components/Common/CanvasForm/Bodies/BodyTypes/BodySpecial/AccountLeadsheet294/utils/constants';

export {FS_TYPE_IDS};

/**
 * Returns an empty normalized leadsheet shape.
 * @returns {object}
 */
export function createEmptyNormalizedState() {
	return {
		trialBalance: null,
		financialStatements: [],
		accountSubClasses: [],
		glAccounts: [],
		references: [],
		tickmarks: [],
		metadata: null,
		isLoading: false,
		isLoaded: false,
		error: null,
		lastFetched: null
	};
}

/**
 * Flattens financial statements, sub-classes, GLs, references, and tickmarks from the API response.
 * GL ids inside each sub-class preserve the server order.
 * @param {object} apiResponse - Raw trial balance response.
 * @returns {object}
 */
function flattenLeadsheetEntities(apiResponse) {
	const fsIds = [];
	const fsItems = [];
	const subClassItems = [];
	const glItems = [];
	const referenceItems = new Map();
	const tickmarkItems = new Map();

	(apiResponse.leadsheet || []).forEach((fs, fsIndex) => {
		const fsId = `fs-${fs.fsTypeId}-${fsIndex}`;
		const subClassIds = [];

		(fs.accSubClassesOrRef || []).forEach((sc, scIndex) => {
			const glIds = [];

			for (const gl of sc.glAccounts || []) {
				const refIds = [];
				const tickmarkIds = [];

				for (const ref of gl.references || []) {
					if (ref?.docId === null || ref?.docId === undefined) continue;
					if (!ref.isArchived) continue;
					refIds.push(ref.docId);
					if (!referenceItems.has(ref.docId)) referenceItems.set(ref.docId, ref);
				}

				for (const tm of gl.tickmarks || []) {
					if (tm?.engTagId === null || tm?.engTagId === undefined) continue;
					tickmarkIds.push(tm.engTagId);
					if (!tickmarkItems.has(tm.engTagId)) tickmarkItems.set(tm.engTagId, tm);
				}

				glIds.push(gl.id);
				glItems.push({...gl, references: refIds, tickmarks: tickmarkIds});
			}

			// Document-grouped responses (viewType:1) use `docId` as the bucket key. The
			// Untested/Incomplete groups have no document, so `docId` is null and `id` is
			// the default 0 for every one of them; fall back to a synthetic per-group key
			// to keep those sub-class/reference entities distinct (a bare `id: 0` would
			// collapse the Untested and Incomplete buckets into one).
			const subClassKey = sc.docId ?? (sc.id ? sc.id : `${fsId}-sc-${scIndex}`);
			// `getReferenceGroupRowLabel` is non-empty ONLY for the docless Untested/Incomplete
			// groups, so it is the safe discriminant for their label; Tested and FS-view
			// sub-classes fall back to `sc.name`.
			const referenceGroupLabel = getReferenceGroupRowLabel(fs.groupByName);
			const subClassName = referenceGroupLabel !== '' ? referenceGroupLabel : sc.name;
			const subClassHasAdjCurr = (sc.glAccounts || []).some((gl) => gl?.hasAdjCurr === true);
			subClassIds.push(subClassKey);
			subClassItems.push({...sc, id: subClassKey, name: subClassName, hasAdjCurr: subClassHasAdjCurr, glAccounts: glIds});
		});

		fsIds.push(fsId);
		fsItems.push({
			id: fsId,
			fsTypeId: fs.fsTypeId,
			groupByName: fs.groupByName,
			accountSubClasses: subClassIds
		});
	});

	return {
		financialStatementIds: fsIds,
		financialStatements: fsItems,
		accountSubClasses: subClassItems,
		glAccounts: glItems,
		references: Array.from(referenceItems.values()),
		tickmarks: Array.from(tickmarkItems.values())
	};
}

/**
 * Builds a lean trial balance entity from the raw API response: every TB-level
 * scalar plus `financialStatements` as the FS adapter id list. The nested
 * `financialStatements` array of full objects is dropped because that data
 * lives in the sibling `fsData` / `accountSubClassData` / `glAccountData`
 * buckets after normalization.
 * @param {object} apiResponse - Raw trial balance response.
 * @param {Array} financialStatementIds - Ordered FS adapter ids.
 * @returns {object}
 */
function buildTrialBalanceEntity(apiResponse, financialStatementIds) {
	// eslint-disable-next-line no-unused-vars
	const {leadsheet: _rawLeadsheet, ...tbScalars} = apiResponse;
	return {
		...tbScalars,
		financialStatements: financialStatementIds
	};
}

/**
 * Builds period and pagination metadata from the API response.
 * @param {object} apiResponse - Raw trial balance response.
 * @returns {object}
 */
function buildMetadata(apiResponse) {
	return {
		currentPeriodDate: apiResponse.currDate,
		comparativePeriodDate: apiResponse.compDate,
		secondComparativePeriodDate: apiResponse.secondDate,
		page: apiResponse.page,
		totalPages: apiResponse.pages,
		totalEntityCount: apiResponse.totalEntities
	};
}

/**
 * Normalizes one trial balance API response into plain bucket arrays plus metadata.
 * The slice converts these arrays into EntityState via adapter `setAll`. The
 * returned `trialBalance` is the lean TB entity that the slice writes into
 * `tbData` — its `financialStatements` field holds only the FS adapter ids.
 * @param {object} apiResponse - Raw trial balance response.
 * @returns {object}
 */
export function normalizeLeadsheetResponse(apiResponse) {
	if (!apiResponse?.leadsheet) {
		return createEmptyNormalizedState();
	}

	const flat = flattenLeadsheetEntities(apiResponse);

	return {
		trialBalance: buildTrialBalanceEntity(apiResponse, flat.financialStatementIds),
		financialStatements: flat.financialStatements,
		accountSubClasses: flat.accountSubClasses,
		glAccounts: flat.glAccounts,
		references: flat.references,
		tickmarks: flat.tickmarks,
		metadata: buildMetadata(apiResponse),
		isLoading: false,
		isLoaded: true,
		error: null,
		lastFetched: new Date().toISOString()
	};
}
